import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { brokerApi, aiApi, marketApi } from './api';
import Watchlist from './Watchlist';
import PriceChart from './PriceChart';
import TickerChartCard from './TickerChartCard';
import StockLink from './StockLink';

function fmtMoney(n) {
  const num = Number(n);
  if (Number.isNaN(num)) return '—';
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function toEquitySeries(history) {
  if (!history?.timestamp) return [];
  return history.timestamp
    .map((ts, i) => ({ time: ts, close: history.equity[i] }))
    .filter((p) => Number.isFinite(p.time) && p.close != null);
}

function ScoreBar({ score }) {
  const color = score >= 66 ? 'var(--green)' : score >= 40 ? 'var(--amber)' : 'var(--red)';
  return (
    <div style={{ background: 'var(--surface-2)', borderRadius: 999, height: 6, overflow: 'hidden' }}>
      <div style={{ width: `${Math.max(0, Math.min(100, score))}%`, background: color, height: '100%' }} />
    </div>
  );
}

export default function Dashboard() {
  const [account, setAccount] = useState(null);
  const [positions, setPositions] = useState(null);
  const [equitySeries, setEquitySeries] = useState(null);
  const [needsBroker, setNeedsBroker] = useState(false);
  const [error, setError] = useState(null);
  const [selectedSymbol, setSelectedSymbol] = useState(null);

  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [healthError, setHealthError] = useState(null);

  const [brief, setBrief] = useState(null);
  const [overview, setOverview] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const [acc, pos] = await Promise.all([brokerApi.account(), brokerApi.positions()]);
        if (!cancelled) {
          setAccount(acc);
          setPositions(pos);
          setNeedsBroker(false);
        }
        brokerApi
          .accountHistory()
          .then((h) => !cancelled && setEquitySeries(toEquitySeries(h)))
          .catch(() => !cancelled && setEquitySeries([]));
      } catch (err) {
        if (cancelled) return;
        if (err.statusCode === 412) setNeedsBroker(true);
        else setError(err.message);
      }
    };

    load();
    const interval = setInterval(load, 20000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    aiApi.dailyBrief().then(setBrief).catch(() => {});
    marketApi.overview().then(setOverview).catch(() => {});
    const interval = setInterval(() => marketApi.overview().then(setOverview).catch(() => {}), 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchHealth = async (pos, acc) => {
    setHealthLoading(true);
    setHealthError(null);
    try {
      const data = await aiApi.portfolioHealth({
        positions: pos,
        cash: Number(acc.cash),
        portfolioValue: Number(acc.portfolio_value),
      });
      setHealth(data);
    } catch (err) {
      setHealthError(err.message);
    } finally {
      setHealthLoading(false);
    }
  };

  useEffect(() => {
    if (positions && account && positions.length > 0 && !health && !healthLoading) {
      fetchHealth(positions, account);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positions, account]);

  if (needsBroker) {
    return (
      <div className="dashboard-container">
        <div className="empty-hero">
          <h2>Connect your broker</h2>
          <p>Connect an Alpaca paper or live account to view balances, positions, orders, and trading tools.</p>
          <Link to="/connect-broker" className="primary-button">
            Connect Alpaca
          </Link>
        </div>
        <section className="dashboard-section">
          <h3>Live Market Watchlist</h3>
          <Watchlist onSelect={(q) => setSelectedSymbol(q.symbol)} selected={selectedSymbol} />
        </section>
        {selectedSymbol && <TickerChartCard symbol={selectedSymbol} />}
      </div>
    );
  }

  const dayChange = account ? Number(account.equity) - Number(account.last_equity) : 0;
  const dayChangePct = account?.last_equity > 0 ? (dayChange / Number(account.last_equity)) * 100 : 0;

  const stats = [
    { label: 'Portfolio Value', value: fmtMoney(account?.portfolio_value) },
    { label: 'Cash', value: fmtMoney(account?.cash) },
    { label: 'Buying Power', value: fmtMoney(account?.buying_power) },
    {
      label: 'Day Change',
      value: account ? `${dayChange >= 0 ? '+' : ''}${fmtMoney(dayChange)} (${dayChangePct.toFixed(2)}%)` : null,
      tone: dayChange >= 0 ? 'up' : 'down',
    },
  ];

  return (
    <div className="dashboard-container">
      <section className="dashboard-header">
        <h2>Portfolio Overview</h2>
        <p className="subtitle">
          Live account data{account ? ` · ${account.status === 'ACTIVE' ? 'Account active' : account.status}` : ''}
          {overview ? ` · Market ${overview.marketState}` : ''}
        </p>
      </section>

      {error && <div className="error-box">{error}</div>}

      <div className="ai-insight-card">
        <span className="ai-badge">✦ AI Daily Brief</span>
        {brief ? <p className="reasoning">{brief.brief}</p> : <span className="skeleton-bar" style={{ width: '100%' }} />}
        <p className="input-hint" style={{ marginTop: 10 }}>
          AI analysis is informational and not financial advice.
        </p>
      </div>

      <div className="stats-grid">
        {stats.map((stat) => (
          <div key={stat.label} className="stat-card">
            <p className="stat-label">{stat.label}</p>
            <p className={`stat-value ${stat.tone || ''}`}>{stat.value ?? <span className="skeleton-bar" />}</p>
          </div>
        ))}
      </div>

      <div className="chart-card">
        <div className="chart-card-header">
          <h3>Equity curve</h3>
        </div>
        {equitySeries === null ? (
          <span className="skeleton-bar" style={{ width: '100%', height: 240 }} />
        ) : equitySeries.length === 0 ? (
          <div className="empty-state">
            <p>No history yet</p>
            <small>Equity history builds up once your account has some trading activity</small>
          </div>
        ) : (
          <PriceChart data={equitySeries} chartType="area" showVolume={false} />
        )}
      </div>

      <section className="dashboard-section">
        <h3>Market Overview</h3>
        {!overview ? (
          <div className="skeleton-list">
            <span className="skeleton-bar" />
          </div>
        ) : (
          <>
            <div className="watchlist" style={{ marginBottom: 14 }}>
              {overview.indices.map((q) => (
                <div key={q.symbol} className="watchlist-row" style={{ cursor: 'default' }}>
                  <StockLink symbol={q.symbol} className="wl-symbol" />
                  <span className="wl-price">${q.price?.toFixed(2)}</span>
                  <span className={`wl-change ${q.change >= 0 ? 'up' : 'down'}`}>
                    {q.change >= 0 ? '▲' : '▼'} {Math.abs(q.changePercent || 0).toFixed(2)}%
                  </span>
                  <span />
                </div>
              ))}
            </div>
            <p className="input-hint" style={{ marginBottom: 8 }}>
              Top movers
            </p>
            <div className="watchlist">
              {overview.topMovers.map((q) => (
                <div key={q.symbol} className="watchlist-row" onClick={() => setSelectedSymbol(q.symbol)}>
                  <StockLink symbol={q.symbol} className="wl-symbol" />
                  <span className="wl-price">${q.price?.toFixed(2)}</span>
                  <span className={`wl-change ${q.change >= 0 ? 'up' : 'down'}`}>
                    {q.change >= 0 ? '▲' : '▼'} {Math.abs(q.changePercent || 0).toFixed(2)}%
                  </span>
                  <span />
                </div>
              ))}
            </div>
          </>
        )}
      </section>

      <div className="dashboard-sections">
        <section className="dashboard-section">
          <h3>Current Positions</h3>
          {!positions ? (
            <div className="skeleton-list">
              <span className="skeleton-bar" />
              <span className="skeleton-bar" />
            </div>
          ) : positions.length === 0 ? (
            <div className="empty-state">
              <p>No open positions</p>
              <small>Head to "Trade" to place your first order</small>
            </div>
          ) : (
            <div className="holdings-list">
              {positions.map((p) => {
                const pl = Number(p.unrealized_pl);
                return (
                  <div
                    key={p.symbol}
                    className="holding-item"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setSelectedSymbol(p.symbol)}
                  >
                    <StockLink symbol={p.symbol} className="holding-ticker" />
                    <span className="holding-qty">{p.qty} shares</span>
                    <span className="holding-value">{fmtMoney(p.market_value)}</span>
                    <span className={`holding-pl ${pl >= 0 ? 'up' : 'down'}`}>
                      {pl >= 0 ? '+' : ''}
                      {fmtMoney(pl)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {selectedSymbol && <TickerChartCard symbol={selectedSymbol} />}
        </section>

        <section className="dashboard-section">
          <h3>Live Market Watchlist</h3>
          <Watchlist onSelect={(q) => setSelectedSymbol(q.symbol)} selected={selectedSymbol} />
        </section>
      </div>

      <section className="dashboard-section">
        <h3>Portfolio Health Score</h3>
        {healthLoading ? (
          <span className="skeleton-bar" style={{ width: '100%' }} />
        ) : healthError ? (
          <div className="error-box">{healthError}</div>
        ) : health ? (
          <>
            <div className="analysis-card score" style={{ marginBottom: 16, textAlign: 'left', padding: 0, boxShadow: 'none', background: 'none' }}>
              <div className="card-value" style={{ fontSize: 40 }}>
                {health.score}
                <span className="card-unit"> /100</span>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {health.factors?.map((f) => (
                <div key={f.label}>
                  <div className="summary-item" style={{ marginBottom: 4 }}>
                    <span>{f.label}</span>
                    <span>{f.score}</span>
                  </div>
                  <ScoreBar score={f.score} />
                  <p className="input-hint" style={{ marginTop: 4 }}>
                    {f.note}
                  </p>
                </div>
              ))}
            </div>
            {health.topRisks?.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <p className="metrics-label" style={{ marginBottom: 8 }}>
                  Top risks
                </p>
                {health.topRisks.map((r) => (
                  <p key={r} className="reasoning" style={{ fontSize: 13, marginBottom: 6 }}>
                    • {r}
                  </p>
                ))}
              </div>
            )}
            <p className="input-hint" style={{ marginTop: 12 }}>
              AI analysis is informational and not financial advice.
            </p>
          </>
        ) : (
          <div className="empty-state">
            <p>No score yet</p>
            <small>Open a position to get an AI-generated portfolio health score</small>
          </div>
        )}
      </section>
    </div>
  );
}
